const swea = require('swea');
const ViewerManager = swea.editor.ViewerManager;
const InteractionManager = swea.editor.InteractionManager;
const InstructionServiceActions = swea.react.actions.InstructionServiceActions;

/**
 * A custom implementation of the best camera interaction.
 */
class InspectVisualizedCamerasInteraction extends swea.editor.interactions.BestCamerasInteraction {
  /**
   * Constructs the interaction.
   * @param { object } options An option object, see {@link BestCamerasInteraction} and below for more info.
   * @param { function } options.callback A callback function called when inspected.
   * @param { boolean } options.radiometric True for radiometric mode, false for visual, undefined if none.
   * @param { number } options.customScale A custom scale factor. Default is 1.0.
   */
  constructor(options) {
    super(options);
    this._callback = options.callback;
    this._radiometric = options.radiometric;
    this._customScale = options.customScale;
    this._disabledInteractionClasses = [];
  }

  /**
   * Initializes the interaction.
   * @param { DOMElement } The element that the interaction works on
   * @param { number } viewerId The viewer ID.
   */
  initialize(element, viewerId) {
    super.initialize(element, viewerId);

    // Add a trigger that closes the viewer or cancels the interaction.
    this.addTrigger(new swea.editor.interactions.EventHandler(
      [swea.editor.interactions.eventCodes.ESC],
      this,
      () => {
        this.terminate();
      })
    );
  }

  /**
   * When the interaction is initialized set the cursor.
   * @param element { DOMElement } The element that the interaction works on
   * @param viewerId { number } The viewerId
   */
  whenInitialized(element, viewerId){
    super.whenInitialized(element, viewerId);
    this._disabledInteractionClasses = InteractionManager.instance().disableContinuousInteractions();
    ViewerManager.instance().getViewerById(viewerId).setCursor('crosshair');
    InstructionServiceActions.showInstruction('instructions.inspect_cameras_interaction');
  }

  /**
   * Open the image viewer when the images have been loaded.
   * Tihs hook is triggered by the user clicking in the model.
   * @param data
   */
  whenCamerasFound(data){
    if (data.cameras.length == 0) {
      return;
    }
    
    // Build per-camera colors: green (most relevant) to red (least relevant)
    const green = [0.1, 1.0, 0.1, 0.9];
    const red = [1.0, 0.1, 0.1, 0.9];
    const cameraColors = new Map();
    const maxIndex = Math.max(data.cameras.length - 1, 1);
    for (let i = 0; i < data.cameras.length; i++) {
      const t = i / maxIndex;
      cameraColors.set(data.cameras[i].name, [
        green[0] + (red[0] - green[0]) * t,
        green[1] + (red[1] - green[1]) * t,
        green[2] + (red[2] - green[2]) * t,
        green[3] + (red[3] - green[3]) * t
      ]);
    }

    this.getSceneViewer().visualizeCameraMatrices(
      undefined,
      undefined,
      false,
      undefined,
      data.cameras,
      this._radiometric,
      this._customScale,
      undefined,
      cameraColors
    );
    if (this._callback !== undefined) {
      this._callback();
    }
    this.terminate();
  }

  whenTerminated(element, viewerId){
    super.whenTerminated(element, viewerId);
    ViewerManager.instance().getViewerById(viewerId).setCursor('default');
    InteractionManager.instance().enableContinuousInteractions(this._disabledInteractionClasses);
    InstructionServiceActions.hideInstruction();
  }
  
  whenCamerasNotFound(){
    InstructionServiceActions.showInstruction('instructions.no_visualized_cameras_available');
  }
}
export default InspectVisualizedCamerasInteraction;
